import type { ReactElement } from 'react';
import { MasteryMeter } from '@/components/molecules/mastery-meter';
import type { MasteryVisualLevel } from '@/lib/mastery';
import { relativeDate } from '@/lib/relative-date';
import { classifyRusty, type RustySeverity } from '@/lib/rusty';

type Severity = { color: string; label: string };

const MASTERY_LABELS: Readonly<Record<MasteryVisualLevel, string>> = {
  new: 'New', learning: 'Learning', solid: 'Solid', fluent: 'Fluent',
};

const SEVERITY_TO_COLOR: Readonly<Record<RustySeverity, string>> = {
  never: 'var(--ink-3)',
  severe: 'var(--err)',
  warning: 'var(--warn)',
  mild: 'var(--warm)',
  fresh: 'var(--ink-4)',
};

function severityFor(days: number | null): Severity {
  return { color: SEVERITY_TO_COLOR[classifyRusty(days)], label: relativeDate(days) };
}

export type RustyItemProps = {
  title: string;
  arrangement?: string;
  days: number | null;
  mastery: MasteryVisualLevel;
  compact?: boolean;
};

export function RustyItem({
  title, arrangement, days, mastery, compact = false,
}: RustyItemProps): ReactElement {
  const severity = severityFor(days);
  return (
    <div className="pl-3 pr-2 py-2 bg-[var(--bg-surface)] border border-[var(--rule)] rounded-[var(--r-md)] relative overflow-hidden">
      <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: severity.color }} aria-hidden />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-display text-[14px] font-semibold text-[var(--ink)] truncate">{title}</div>
          {arrangement && (
            <div className="text-[11px] text-[var(--ink-3)] truncate">{arrangement}</div>
          )}
        </div>
        {!compact && <MasteryMeter level={mastery} showLabel={false} size="sm" />}
      </div>
      <div className="flex items-center justify-between mt-1">
        <span className="text-[10px] font-mono" style={{ color: severity.color }}>{severity.label}</span>
        {!compact && <span className="text-[10px] text-[var(--ink-3)]">{MASTERY_LABELS[mastery]}</span>}
      </div>
    </div>
  );
}
